import { Classes, Intent } from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import { DOCS_LINKS, UiSchema, VcsConnectionType } from "@hex/common";
import { JSONSchemaType } from "ajv";
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import {
  HexButton,
  HexCallout,
  HexDialog,
  HexMenuItem,
  HexRichSwitch,
} from "../../hex-components";
import { DocsLink } from "../common/DocsLink";
import { Form } from "../common/form/Form";
import {
  GitHubIcon,
  GitLabIcon,
  LockIcon,
  SingleChevronDownIcon,
} from "../icons/CustomIcons";

import { SyncRepoFragment } from "./queries.generated";

const HexDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RepoSelectContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const RepoSelectTitle = styled.div`
  margin-right: 15px;
  padding: 5px 0px;
`;

const RepoSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const LockCallout = styled.div`
  display: flex;
  gap: 5px;

  padding: 10px;

  color: ${({ theme }) => theme.fontColor.MUTED};

  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const RepoDiv = styled.div`
  display: flex;
  align-items: center;
`;

export type RemoteFilePathFormData = {
  path: string;
};

const FORM_SCHEMA: JSONSchemaType<RemoteFilePathFormData> = {
  type: "object",
  required: ["path"],
  properties: {
    path: {
      $id: "#/properties/path",
      type: "string",
      regexp: "/^[a-z0-9_\\-]+[a-z0-9_\\-/]*\\.(yaml|yml)$/i",
      errorMessage:
        "Must be a valid path to a .yaml file, using only alphanumeric characters and hyphens. Must not contain a leading slash.",
    },
  },
};

const UI_SCHEMA: UiSchema = {
  sections: [
    {
      fields: [
        {
          id: "path",
          displayName: "Path",
          type: "string",
          helpText:
            "Enter a valid path to a .yaml file on the remote repository",
        },
      ],
    },
  ],
};
export interface SyncSettingsDialogProps {
  dialogOpen: boolean;
  closeDialog: () => void;
  formRef: MutableRefObject<Form<RemoteFilePathFormData> | null>;
  dialogData: RemoteFilePathFormData;
  onChangeForm: (newFormData: RemoteFilePathFormData) => void;
  submitForm: () => void;
  syncRepos: SyncRepoFragment[];
  currentRepo: SyncRepoFragment;
  sameFile?: boolean;
  error?: string;
  fileExists?: boolean;
  selectedRepo?: SyncRepoFragment;
  setSelectedRepo: (repo: SyncRepoFragment) => void;
  loading: boolean;
}

export const SyncSettingsDialog: React.ComponentType<SyncSettingsDialogProps> =
  React.memo(function SyncSettingsDialog({
    closeDialog,
    currentRepo,
    dialogData,
    dialogOpen,
    error,
    fileExists,
    formRef,
    loading,
    onChangeForm,
    sameFile,
    selectedRepo,
    setSelectedRepo,
    submitForm,
    syncRepos,
  }) {
    const repoRenderer = useCallback((repo: SyncRepoFragment) => {
      return (
        <RepoDiv>
          {`${repo.repoOwner}/${repo.repoName}`}
          {repo.requirePullRequests && (
            <LockIcon
              css={`
                margin-left: 4px !important;
              `}
            />
          )}
        </RepoDiv>
      );
    }, []);

    const [repoType, setRepoType] = useState<"github" | "gitlab" | null>();
    const githubRepos = useMemo(() => {
      return syncRepos.filter(
        (repo) => repo.vcsConnectionType === VcsConnectionType.GITHUB_SYNC,
      );
    }, [syncRepos]);

    const gitlabRepos = useMemo(() => {
      return syncRepos.filter(
        (repo) => repo.vcsConnectionType === VcsConnectionType.GITLAB_SYNC,
      );
    }, [syncRepos]);

    const hasGitlabAndGithub = useMemo(
      () => githubRepos.length > 0 && gitlabRepos.length > 0,
      [githubRepos.length, gitlabRepos.length],
    );

    // Set RepoType if unset
    useEffect(() => {
      if (currentRepo && !repoType) {
        setRepoType(
          currentRepo.vcsConnectionType === VcsConnectionType.GITLAB_SYNC
            ? "gitlab"
            : "github",
        );
      } else if (!repoType) {
        setRepoType("github");
      }
    }, [currentRepo, repoType]);

    useEffect(() => {
      if (repoType === "github") {
        setSelectedRepo(githubRepos[0]);
      } else if (repoType === "gitlab") {
        setSelectedRepo(gitlabRepos[0]);
      }
    }, [currentRepo, githubRepos, gitlabRepos, repoType, setSelectedRepo]);

    const repoMenuItemRenderer: ItemRenderer<SyncRepoFragment> = useCallback(
      (repo, itemProps) => (
        <HexMenuItem
          key={repo.id}
          active={itemProps.modifiers.active}
          disabled={itemProps.modifiers.disabled}
          text={repoRenderer(repo)}
          onClick={itemProps.handleClick}
        />
      ),
      [repoRenderer],
    );

    return (
      <HexDialog
        isOpen={dialogOpen}
        title="Git Sync Settings"
        onClose={closeDialog}
      >
        <HexDialogBody className={Classes.DIALOG_BODY}>
          <div>
            Select the git repository and location that you will be syncing your
            project to.{" "}
            <DocsLink to={DOCS_LINKS.GitHubSync}>
              Learn more about git sync
            </DocsLink>
            .
          </div>
          <RepoSelectContainer>
            <RepoSelectTitle>Repository</RepoSelectTitle>
            <RepoSelectWrapper>
              {hasGitlabAndGithub && (
                <HexRichSwitch
                  options={[
                    {
                      icon: <GitHubIcon />,
                      id: "github",
                      text: "GitHub",
                      active: repoType === "github",
                      onClick: () => setRepoType("github"),
                    },
                    {
                      icon: <GitLabIcon />,
                      id: "gitlab",
                      text: "GitLab",
                      active: repoType === "gitlab",
                      onClick: () => setRepoType("gitlab"),
                    },
                  ]}
                  small={true}
                  type="data-select"
                />
              )}
              <Select<SyncRepoFragment>
                filterable={false}
                itemRenderer={repoMenuItemRenderer}
                items={repoType === "github" ? githubRepos : gitlabRepos}
                popoverProps={{ minimal: true, fill: true }}
                onItemSelect={setSelectedRepo}
              >
                <HexButton
                  css={`
                    width: 100%;
                    justify-content: space-between;
                  `}
                  fill={true}
                  rightIcon={<SingleChevronDownIcon />}
                  text={selectedRepo ? repoRenderer(selectedRepo) : ""}
                />
              </Select>
            </RepoSelectWrapper>
          </RepoSelectContainer>
          <Form<RemoteFilePathFormData>
            ref={formRef}
            formData={dialogData}
            jsonSchema={FORM_SCHEMA}
            uiSchema={UI_SCHEMA}
            onChange={onChangeForm}
          />
          {selectedRepo && selectedRepo.requirePullRequests && (
            <LockCallout>
              <LockIcon />
              <div>
                This sync repo requires pull requests to merge to the main
                branch.
              </div>
            </LockCallout>
          )}
          {error && (
            <HexCallout intent={fileExists ? Intent.WARNING : Intent.DANGER}>
              {error}
            </HexCallout>
          )}
        </HexDialogBody>
        <div
          className={Classes.DIALOG_FOOTER}
          css={`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
          `}
        >
          <HexButton onClick={closeDialog}>Cancel</HexButton>
          <HexButton
            disabled={
              loading ||
              !dialogData.path ||
              (sameFile && currentRepo.id == selectedRepo?.id) ||
              (!!error && !fileExists)
            }
            intent={fileExists && !sameFile ? Intent.WARNING : Intent.PRIMARY}
            loading={loading}
            onClick={submitForm}
          >
            {fileExists
              ? "Yep, I'm sure I want to use this path"
              : "Save settings"}
          </HexButton>
        </div>
      </HexDialog>
    );
  });
